import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { InternalBackButton, InternalPageHeader } from "@/components/InternalPageHeader";
import {
  ClipboardList, Plus, Filter, Search, RefreshCw, Download,
  CheckCircle, XCircle, Clock, AlertTriangle, TrendingUp,
  ChevronRight, X, Edit2, RotateCcw, Archive, Users,
  Calendar, Hash, Package, BarChart2, AlertCircle,
  FileText, Loader2, ChevronDown, Bell, MoreHorizontal
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast as sonerToast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== COLOR HELPERS ====================
const TYPE_COLOR = {
  reservation: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-400", dot: "bg-blue-500", label: "Reservation" },
  lpo_received: { bg: "bg-red-100", text: "text-red-700", border: "border-red-400", dot: "bg-red-500", label: "LPO Received" },
};

const STATUS_COLOR = {
  pending_approval: { bg: "bg-amber-100", text: "text-amber-700", label: "Pending" },
  active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
  rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejected" },
  expired: { bg: "bg-gray-100", text: "text-gray-600", label: "Expired" },
  closed: { bg: "bg-slate-100", text: "text-slate-600", label: "Closed" },
  cancelled: { bg: "bg-gray-100", text: "text-gray-500", label: "Cancelled" },
};

const AGEING_COLOR = {
  green: "text-green-600 bg-green-50",
  yellow: "text-amber-600 bg-amber-50",
  red: "text-red-600 bg-red-50",
};

function TypeBadge({ type }) {
  const c = TYPE_COLOR[type] || TYPE_COLOR.reservation;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
      {c.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || { bg: "bg-gray-100", text: "text-gray-600", label: status };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>{c.label}</span>;
}

function AgeingBadge({ days, color }) {
  const cls = AGEING_COLOR[color] || AGEING_COLOR.green;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{days}d</span>;
}

function DupBadge() {
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700"><AlertTriangle className="h-3 w-3" />Dup Alert</span>;
}

// ==================== MAIN PAGE ====================
export default function ReservationsPage({ userRole, onLogout, userSession, onClearNotifs }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [reservations, setReservations] = useState([]);
  const [sortBy, setSortBy] = useState("created_at"); // #18 priority queue sort
  const [pendingList, setPendingList] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [dashboard, setDashboard] = useState(null);
  const [salesmen, setSalesmen] = useState([]);
  const [stockSummary, setStockSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [dashLoading, setDashLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingRes, setEditingRes] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [renewModal, setRenewModal] = useState(null);
  const [manageModal, setManageModal] = useState(false);
  const [soMatchModal, setSoMatchModal] = useState(false);
  const [soMatches, setSoMatches] = useState([]);
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [sessionSalesman, setSessionSalesman] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [filters, setFilters] = useState({ search: "", type: "", status: "", salesman: "", dateFrom: "", dateTo: "" });
  const [histPage, setHistPage] = useState(1);
  const isAdmin = userRole === "admin";
  const isLogistics = userRole === "logistics";
  const isStaff = userRole === "staff";
  const canManage = isAdmin || isLogistics;

  // ==================== DATA FETCHING ====================
  const fetchDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const r = await axios.get(`${API}/reservations/dashboard-summary`);
      setDashboard(r.data);
      if (r.data.needs_identity) setNeedsIdentity(true);
    } catch (e) {
      console.error(e);
    } finally {
      setDashLoading(false);
    }
  }, []);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.salesman) params.salesman = filters.salesman;
      if (filters.search) params.search = filters.search;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      const r = await axios.get(`${API}/reservations`, { params });
      if (r.data.needs_identity) { setNeedsIdentity(true); return; }
      setReservations(r.data.reservations || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchPending = useCallback(async () => {
    if (!canManage) return;
    try {
      const r = await axios.get(`${API}/reservations`, { params: { status: "pending_approval", page_size: 100 } });
      setPendingList(r.data.reservations || []);
    } catch (e) { console.error(e); }
  }, [canManage]);

  const fetchHistory = useCallback(async (page = 1) => {
    try {
      const r = await axios.get(`${API}/reservations/history`, { params: { page, page_size: 50 } });
      setHistory(r.data.history || []);
      setHistoryTotal(r.data.total || 0);
      setHistPage(page);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSalesmen = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/salesmen`);
      setSalesmen(r.data.salesmen || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchStockSummary = useCallback(async () => {
    if (!canManage) return;
    try {
      const r = await axios.get(`${API}/reservations/stock-summary`);
      setStockSummary(r.data || {});
    } catch (e) { console.error(e); }
  }, [canManage]);

  const fetchSession = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      if (r.data.salesman_name) {
        setSessionSalesman(r.data.salesman_name);
        setNeedsIdentity(false); // explicitly clear in case fetchDashboard raced ahead
      } else if (r.data.role === "staff") {
        setNeedsIdentity(true);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchSession();
    fetchSalesmen();
    fetchDashboard();
    if (canManage) fetchStockSummary();
    // Clear notification badge when user visits Reservations page
    if (onClearNotifs) onClearNotifs();
    // #16 Quick Reserve: auto-open form if ?product=CODE in URL
    const params = new URLSearchParams(location.search);
    const preProduct = params.get("product");
    if (preProduct) {
      setActiveTab("all");
      setShowForm(true);
      setEditingRes({ product_code: preProduct, __prefilled: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === "all" || (isStaff && activeTab === "my-requests")) fetchReservations();
    if (activeTab === "pending") fetchPending();
    if (activeTab === "history") fetchHistory(1);
  }, [activeTab, fetchReservations, fetchPending, fetchHistory, isStaff]);

  // ==================== ACTIONS ====================
  const handleApprove = async (id) => {
    setActionLoading(id + "_approve");
    try {
      await axios.post(`${API}/reservations/${id}/approve`);
      toast.success("Reservation approved");
      fetchPending(); fetchDashboard(); if (activeTab === "all") fetchReservations();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
    finally { setActionLoading(""); }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(id + "_reject");
    try {
      await axios.post(`${API}/reservations/${id}/reject`, { rejection_reason: reason });
      toast.success("Reservation rejected");
      setRejectModal(null);
      fetchPending(); fetchDashboard(); if (activeTab === "all") fetchReservations();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reject"); }
    finally { setActionLoading(""); }
  };

  const handleClose = async (id) => {
    setActionLoading(id + "_close");
    try {
      await axios.post(`${API}/reservations/${id}/close`);
      toast.success("Reservation closed");
      fetchReservations(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to close"); }
    finally { setActionLoading(""); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this reservation? This cannot be undone.")) return;
    setActionLoading(id + "_cancel");
    try {
      await axios.post(`${API}/reservations/${id}/cancel`);
      toast.success("Reservation cancelled");
      fetchReservations(); fetchPending(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
    finally { setActionLoading(""); }
  };

  const handleRenew = async (id, days) => {
    setActionLoading(id + "_renew");
    try {
      await axios.post(`${API}/reservations/${id}/renew`, { extension_days: days });
      toast.success(`Extended by ${days} day(s)`);
      setRenewModal(null);
      fetchReservations(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to renew"); }
    finally { setActionLoading(""); }
  };

  const handleDismissFlag = async (id) => {
    try {
      await axios.post(`${API}/reservations/${id}/dismiss-flag`);
      toast.success("Duplicate flag dismissed");
      fetchReservations(); fetchDashboard();
    } catch (e) { toast.error("Failed to dismiss flag"); }
  };

  const handleConfirmClose = async (id) => {
    setActionLoading(id + "_confirm");
    try {
      await axios.post(`${API}/reservations/${id}/confirm-close`);
      toast.success("Reservation closed (ERP match confirmed)");
      setSoMatches(prev => prev.filter(m => m.reservation_id !== id));
      fetchReservations(); fetchDashboard();
    } catch (e) { toast.error("Failed to confirm closure"); }
    finally { setActionLoading(""); }
  };

  const handleExport = async (fmt) => {
    try {
      const params = new URLSearchParams({ format: fmt });
      if (filters.status) params.set("status", filters.status);
      if (filters.type) params.set("type", filters.type);
      if (filters.salesman) params.set("salesman", filters.salesman);
      const res = await fetch(`${API}/reservations/export?${params}`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `Reservations.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const handleDetectSoMatches = async () => {
    try {
      const r = await axios.get(`${API}/reservations/detect-so-matches`);
      setSoMatches(r.data.matches || []);
      setSoMatchModal(true);
    } catch (e) { toast.error("Failed to check ERP matches"); }
  };

  const handleSelectSalesman = async (id) => {
    try {
      const r = await axios.post(`${API}/auth/select-salesman`, { salesman_id: id });
      setSessionSalesman(r.data.salesman_name);
      setNeedsIdentity(false);
      toast.success(`Identity set: ${r.data.salesman_name}`);
      fetchDashboard(); fetchReservations();
    } catch (e) { toast.error("Failed to set identity"); }
  };

  // ==================== TABS CONFIG ====================
  const tabs = canManage
    ? [
        { id: "dashboard", label: "Dashboard", icon: BarChart2 },
        { id: "all", label: "All", icon: ClipboardList },
        { id: "pending", label: "Pending", icon: Clock, badge: pendingList.length || (dashboard?.total_pending || 0) },
        { id: "history", label: "History", icon: FileText },
      ]
    : [
        { id: "dashboard", label: "My Summary", icon: BarChart2 },
        { id: "my-requests", label: "My Requests", icon: ClipboardList },
        { id: "history", label: "My History", icon: FileText },
      ];

  const pendingBadge = canManage ? (dashboard?.total_pending || 0) : 0;

  const handleBack = () => {
    navigate("/stock");
  };

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-[#F5F4F1] flex flex-col">
      <InternalPageHeader
        title="Reservations"
        subtitle={isAdmin ? "Admin View" : isLogistics ? "Logistics View" : `Staff · ${sessionSalesman || "Select Identity"}`}
        onBack={handleBack}
        backTestId="reservations-back-button"
        leading={<img src="/logo.jpeg" alt="RM" className="w-7 h-7 rounded" />}
        actions={
          <>
            {canManage && pendingBadge > 0 && (
              <button onClick={() => setActiveTab("pending")} className="relative" data-testid="pending-badge-btn">
                <Bell className="h-5 w-5 text-amber-400" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{pendingBadge}</span>
              </button>
            )}
            <Button onClick={() => { setEditingRes(null); setShowForm(true); }} size="sm"
              className="bg-[#DC2626] hover:bg-[#b91c1c] text-white text-xs gap-1" data-testid="new-reservation-btn">
              <Plus className="h-3.5 w-3.5" /> New
            </Button>
          </>
        }
      />

      {/* Identity Modal (Staff only) */}
      {needsIdentity && isStaff && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-[#09090b] mb-1">Who are you?</h2>
            <p className="text-sm text-gray-500 mb-4">Select your name to link your session to your reservations.</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {salesmen.map(s => (
                <button key={s.salesman_id} onClick={() => handleSelectSalesman(s.salesman_id)}
                  className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-[#DC2626] hover:bg-red-50 transition-all text-sm font-medium"
                  data-testid={`identity-${s.salesman_id}`}>
                  {s.name} {s.email && <span className="text-xs text-gray-400 ml-1">({s.email})</span>}
                </button>
              ))}
              {salesmen.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No salesmen configured. Contact admin.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-14 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center gap-1 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0
                  ${activeTab === tab.id ? "border-[#DC2626] text-[#DC2626]" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center">{tab.badge}</span>
                )}
              </button>
            );
          })}
          {canManage && (
            <div className="ml-auto flex items-center gap-2 py-2 pl-4 border-l border-gray-100 flex-shrink-0">
              <button onClick={fetchDashboard} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              {/* ACTIONS dropdown — replaces the 7-button toolbar */}
              <div className="relative">
                <button
                  data-testid="reservations-actions-btn"
                  onClick={() => setShowActionsMenu(v => !v)}
                  className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${showActionsMenu ? 'bg-[#09090b] text-white border-[#09090b]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                  Actions
                  <ChevronDown className={`h-3 w-3 transition-transform ${showActionsMenu ? 'rotate-180' : ''}`} />
                </button>
                {showActionsMenu && (
                  <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-xl border border-[#E5E7EB] py-1.5 z-50 min-w-[170px]">
                    <p className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold">Export</p>
                    <button onClick={() => { handleExport("csv"); setShowActionsMenu(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F3F4F6] transition-colors">
                      <Download className="h-4 w-4 text-[#6B7280]" />CSV
                    </button>
                    <button onClick={() => { handleExport("xlsx"); setShowActionsMenu(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F3F4F6] transition-colors">
                      <Download className="h-4 w-4 text-[#6B7280]" />Excel
                    </button>
                    <button onClick={() => { handleExport("pdf"); setShowActionsMenu(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F3F4F6] transition-colors">
                      <Download className="h-4 w-4 text-[#6B7280]" />PDF
                    </button>
                    {canManage && (
                      <a href={`${API}/reservations/export/picking-list?format=xlsx`} download
                        data-testid="picking-list-export-btn"
                        onClick={() => setShowActionsMenu(false)}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors">
                        <Download className="h-4 w-4" />Pick List
                      </a>
                    )}
                    <div className="border-t border-[#F3F4F6] my-1" />
                    <p className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold">Manage</p>
                    {isAdmin && (
                      <button onClick={() => { setManageModal(true); setShowActionsMenu(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F3F4F6] transition-colors">
                        <Users className="h-4 w-4 text-[#6B7280]" />Salesmen
                      </button>
                    )}
                    <button onClick={() => { handleDetectSoMatches(); setShowActionsMenu(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 transition-colors">
                      <AlertCircle className="h-4 w-4" />ERP Match
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-6 pb-20 bottom-nav-safe-padding">
        {activeTab === "dashboard" && <DashboardTab dashboard={dashboard} loading={dashLoading} isStaff={isStaff} sessionSalesman={sessionSalesman} onNewRequest={() => { setEditingRes(null); setShowForm(true); }} stockSummary={stockSummary} canManage={canManage} />}
        {activeTab === "all" && <AllTab reservations={reservations} loading={loading} filters={filters} setFilters={setFilters} salesmen={salesmen} onApprove={handleApprove} onReject={(r) => setRejectModal(r)} onClose={handleClose} onCancel={handleCancel} onRenew={(r) => setRenewModal(r)} onEdit={(r) => { setEditingRes(r); setShowForm(true); }} onDismissFlag={handleDismissFlag} actionLoading={actionLoading} canManage={canManage} onRefresh={fetchReservations} isStaff={isStaff} />}
        {(activeTab === "my-requests" && isStaff) && <AllTab reservations={reservations} loading={loading} filters={filters} setFilters={setFilters} salesmen={salesmen} onApprove={handleApprove} onReject={(r) => setRejectModal(r)} onClose={handleClose} onCancel={handleCancel} onRenew={(r) => setRenewModal(r)} onEdit={(r) => { setEditingRes(r); setShowForm(true); }} onDismissFlag={handleDismissFlag} actionLoading={actionLoading} canManage={false} onRefresh={fetchReservations} isStaff={true} />}
        {activeTab === "pending" && canManage && <PendingTab reservations={pendingList} onApprove={handleApprove} onReject={(r) => setRejectModal(r)} actionLoading={actionLoading} onRefresh={fetchPending} />}
        {activeTab === "history" && <HistoryTab history={history} total={historyTotal} page={histPage} onPageChange={fetchHistory} canManage={canManage} isStaff={isStaff} />}
      </main>

      {/* Modals */}
      {showForm && (
        <ReservationFormModal
          isOpen={showForm} onClose={() => { setShowForm(false); setEditingRes(null); }}
          editingRes={editingRes} salesmen={salesmen} sessionSalesman={sessionSalesman}
          userRole={userRole} canManage={canManage}
          onSuccess={() => { fetchReservations(); fetchPending(); fetchDashboard(); setShowForm(false); setEditingRes(null); }}
        />
      )}
      {rejectModal && (
        <RejectModal reservation={rejectModal} onClose={() => setRejectModal(null)} onConfirm={handleReject} loading={actionLoading.includes("_reject")} />
      )}
      {renewModal && (
        <RenewModal reservation={renewModal} onClose={() => setRenewModal(null)} onConfirm={handleRenew} loading={actionLoading.includes("_renew")} />
      )}
      {manageModal && isAdmin && (
        <SalesmanManageModal salesmen={salesmen} onClose={() => setManageModal(false)} onRefresh={fetchSalesmen} />
      )}
      {soMatchModal && (
        <SOMatchModal matches={soMatches} onClose={() => setSoMatchModal(false)} onConfirm={handleConfirmClose} actionLoading={actionLoading} />
      )}
    </div>
  );
}

// ==================== DASHBOARD TAB ====================
function DashboardTab({ dashboard, loading, isStaff, sessionSalesman, onNewRequest, stockSummary, canManage }) {
  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" /></div>;
  if (!dashboard) return null;

  if (isStaff) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "My Active", value: dashboard.my_active || 0, color: "text-green-600", icon: CheckCircle },
            { label: "Pending Approval", value: dashboard.my_pending || 0, color: "text-amber-600", icon: Clock },
            { label: "Expiring Soon", value: dashboard.my_expiring || 0, color: "text-red-600", icon: AlertTriangle },
            { label: "Rejected", value: dashboard.my_rejected || 0, color: "text-gray-500", icon: XCircle },
          ].map(c => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-1"><Icon className={`h-4 w-4 ${c.color}`} /><p className="text-xs text-gray-500">{c.label}</p></div>
                <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
              </div>
            );
          })}
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
          <p className="text-sm text-gray-500 mb-3">Logged in as <span className="font-semibold text-[#09090b]">{sessionSalesman || "—"}</span></p>
          <Button onClick={onNewRequest} className="bg-[#DC2626] hover:bg-[#b91c1c] text-white gap-2" data-testid="dashboard-new-request-btn">
            <Plus className="h-4 w-4" /> New Reservation Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Active", value: dashboard.total_active || 0, color: "text-green-600", accent: "#059669" },
          { label: "Pending", value: dashboard.total_pending || 0, color: "text-amber-600", accent: "#ca8a04" },
          { label: "Overdue", value: dashboard.total_overdue || 0, color: "text-red-600", accent: "#dc2626" },
          { label: "Exp. Soon", value: dashboard.expiring_soon || 0, color: "text-orange-600", accent: "#ea580c" },
          { label: "Dup. Alerts", value: dashboard.duplicate_alerts || 0, color: "text-orange-500", accent: "#f97316" },
          { label: "ERP Matches", value: dashboard.auto_match_pending || 0, color: "text-blue-600", accent: "#2563eb" },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 border-l-[3px] transition-transform hover:-translate-y-0.5 hover:shadow-md"
            style={{ borderLeftColor: c.accent }}>
            <p className="text-[11px] text-gray-500 mb-0.5">{c.label}</p>
            <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Type breakdown */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-l-blue-400">
          <p className="text-xs text-gray-500 mb-1">Active Reservations</p>
          <p className="text-3xl font-bold text-blue-600">{dashboard.reservations_count || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-l-red-400">
          <p className="text-xs text-gray-500 mb-1">LPO Received</p>
          <p className="text-3xl font-bold text-red-600">{dashboard.lpo_count || 0}</p>
        </div>
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Products */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-[#09090b] mb-1 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#DC2626]" />Top Reserved Products</h3>
          <p className="text-xs text-[#6B7280] mb-3" data-testid="top-reserved-products-description">
            Shows which products currently have the highest reserved quantities.
          </p>
          {(dashboard.top_products || []).length === 0 ? (
            <div className="flex flex-col items-center py-6 text-center">
              <ClipboardList className="h-8 w-8 text-gray-300 mb-2" />
              <p className="text-xs text-gray-400">No active reservations yet</p>
              <p className="text-[10px] text-gray-300 mt-0.5">Reservations will appear here once created</p>
            </div>
          ) : (
            <div className="space-y-2">
              {dashboard.top_products.map((p, i) => (
                <div key={p.product_code} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 font-medium truncate flex-1">{p.product_code}</span>
                  <span className="text-[#09090b] font-bold ml-2">{p.total_qty?.toFixed(1)} {p.unit}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* By Salesman */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-[#09090b] mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-[#DC2626]" />By Salesman</h3>
          {(dashboard.by_salesman || []).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No data</p>
          ) : (
            <div className="space-y-2">
              {dashboard.by_salesman.map(s => (
                <div key={s.salesman} className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 flex-1 truncate">{s.salesman || "—"}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[80px]">
                    <div className="bg-[#DC2626] h-1.5 rounded-full" style={{ width: `${Math.min(100, (s.count / ((dashboard.total_active || 1) || 1)) * 100)}%` }}></div>
                  </div>
                  <span className="text-xs font-bold text-[#09090b] w-5 text-right">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== ALL RESERVATIONS TAB ====================
function AllTab({ reservations, loading, filters, setFilters, salesmen, onApprove, onReject, onClose, onCancel, onRenew, onEdit, onDismissFlag, actionLoading, canManage, onRefresh, isStaff }) {
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState("created_at"); // #18 priority queue

  // #18 Priority Queue: sort by expiry (urgent first) or creation date
  const sorted = [...reservations].sort((a, b) => {
    if (sortBy === "expiry") {
      const da = a.expiry_date ? new Date(a.expiry_date) : new Date("9999-01-01");
      const db2 = b.expiry_date ? new Date(b.expiry_date) : new Date("9999-01-01");
      return da - db2;
    }
    if (sortBy === "quantity") return (b.quantity || 0) - (a.quantity || 0);
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1 flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-1.5">
            <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <input placeholder="Search product, customer, batch…" value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="text-sm outline-none flex-1 min-w-0 bg-transparent" data-testid="res-search-input" />
          </div>
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none bg-white" data-testid="res-status-filter">
            <option value="">All Status</option>
            <option value="pending_approval">Pending</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none bg-white" data-testid="res-type-filter">
            <option value="">All Types</option>
            <option value="reservation">Reservation</option>
            <option value="lpo_received">LPO Received</option>
          </select>
          {canManage && (
            <select value={filters.salesman} onChange={e => setFilters(f => ({ ...f, salesman: e.target.value }))}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none bg-white" data-testid="res-salesman-filter">
              <option value="">All Salesmen</option>
              {salesmen.map(s => <option key={s.salesman_id} value={s.name}>{s.name}</option>)}
            </select>
          )}
          {/* #18 Priority Queue Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none bg-white" data-testid="res-sort-select">
            <option value="created_at">Newest First</option>
            <option value="expiry">Expiry (Urgent First)</option>
            <option value="quantity">Quantity (High to Low)</option>
          </select>
          <button onClick={onRefresh} className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500" data-testid="res-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#DC2626]" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No reservations found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(r => (
            <ReservationCard key={r.reservation_id} reservation={r} canManage={canManage} isStaff={isStaff}
              onApprove={onApprove} onReject={onReject} onClose={onClose} onCancel={onCancel}
              onRenew={onRenew} onEdit={onEdit} onDismissFlag={onDismissFlag}
              actionLoading={actionLoading} expanded={expandedId === r.reservation_id}
              onExpand={() => setExpandedId(prev => prev === r.reservation_id ? null : r.reservation_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== RESERVATION CARD ====================
function ReservationCard({ reservation: r, canManage, isStaff, onApprove, onReject, onClose, onCancel, onRenew, onEdit, onDismissFlag, actionLoading, expanded, onExpand }) {
  const typeCol = TYPE_COLOR[r.type] || TYPE_COLOR.reservation;
  const isExpiring = r.expiry_status === "critical" || r.expiry_status === "warning";
  const isExpired = r.status === "expired";

  const statusBorderClass = {
    active: "border-l-green-500",
    pending_approval: "border-l-amber-400",
    expired: "border-l-red-400",
    rejected: "border-l-red-400",
    overdue: "border-l-red-500",
    closed: "border-l-gray-300",
    cancelled: "border-l-gray-200",
  }[r.status] || "border-l-gray-200";

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-[5px] ${statusBorderClass} transition-all active:bg-gray-50 ${isExpired ? "opacity-60" : ""}`}
      data-testid={`res-card-${r.reservation_id}`}>
      <div className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Left info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <TypeBadge type={r.type} />
              <StatusBadge status={r.status} />
              {r.duplicate_flag === "suspected" && <DupBadge />}
              {isExpiring && r.status === "active" && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  r.days_until_expiry <= 0 ? "bg-red-600 text-white" :
                  r.days_until_expiry <= 1 ? "bg-red-100 text-red-700" :
                  "bg-amber-50 text-amber-700"
                }`}>
                  {r.days_until_expiry <= 0 ? "Expires today!" : `Exp. in ${r.days_until_expiry}d`}
                </span>
              )}
            </div>
            <h3 className="font-semibold text-[#09090b] text-sm sm:text-base truncate">{r.product_code}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-500">
              {r.batch_code && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{r.batch_code}</span>}
              <span className="flex items-center gap-1"><Package className="h-3 w-3" />{r.quantity} {r.unit}</span>
              {r.salesman && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{r.salesman}</span>}
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{r.commitment_date?.slice(0, 10)}</span>
              {r.ageing_days !== undefined && <AgeingBadge days={r.ageing_days} color={r.ageing_color} />}
            </div>
            {r.customer_name && r.customer_name !== "[Confidential]" && (
              <p className="text-xs text-gray-600 mt-1 font-medium">{r.customer_name}</p>
            )}
            {r.lpo_number && <p className="text-xs text-blue-600 mt-0.5">LPO: {r.lpo_number}</p>}
          </div>
          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={onExpand} className="p-1.5 hover:bg-gray-100 rounded-lg">
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {/* Expanded actions */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {r.notes && <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg">{r.notes}</p>}
            {r.rejection_reason && <p className="text-xs text-red-600 bg-red-50 p-2 rounded-lg">Rejection: {r.rejection_reason}</p>}
            <div className="text-xs text-gray-400 space-y-0.5">
              <p>Created by: {r.created_by} · {r.created_at?.slice(0, 10)}</p>
              {r.approved_by && <p>Approved by: {r.approved_by} · {r.approved_at?.slice(0, 10)}</p>}
              {r.expiry_date && r.status === "active" && <p>Expires: {r.expiry_date?.slice(0, 10)}</p>}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {canManage && r.status === "pending_approval" && (
                <>
                  <button onClick={() => onApprove(r.reservation_id)} disabled={!!actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg font-medium"
                    data-testid={`approve-${r.reservation_id}`}>
                    {actionLoading === r.reservation_id + "_approve" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />} Approve
                  </button>
                  <button onClick={() => onReject(r)} disabled={!!actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg font-medium"
                    data-testid={`reject-${r.reservation_id}`}>
                    <XCircle className="h-3 w-3" /> Reject
                  </button>
                </>
              )}
              {canManage && r.status === "active" && (
                <>
                  <button onClick={() => onClose(r.reservation_id)} disabled={!!actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white text-xs rounded-lg font-medium"
                    data-testid={`close-${r.reservation_id}`}>
                    <Archive className="h-3 w-3" /> Close
                  </button>
                  <button onClick={() => onRenew(r)} disabled={!!actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 border border-blue-400 text-blue-600 hover:bg-blue-50 text-xs rounded-lg font-medium">
                    <RotateCcw className="h-3 w-3" /> Renew
                  </button>
                </>
              )}
              {canManage && r.status === "expired" && (
                <button onClick={() => onRenew(r)} disabled={!!actionLoading}
                  className="flex items-center gap-1 px-3 py-1.5 border border-blue-400 text-blue-600 hover:bg-blue-50 text-xs rounded-lg font-medium"
                  data-testid={`renew-expired-${r.reservation_id}`}>
                  <RotateCcw className="h-3 w-3" /> Renew
                </button>
              )}
              {canManage && !["closed", "cancelled"].includes(r.status) && (
                <>
                  <button onClick={() => onEdit(r)}
                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 text-xs rounded-lg font-medium"
                    data-testid={`edit-${r.reservation_id}`}>
                    <Edit2 className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => onCancel(r.reservation_id)} disabled={!!actionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 text-xs rounded-lg font-medium"
                    data-testid={`cancel-${r.reservation_id}`}>
                    <X className="h-3 w-3" /> Cancel
                  </button>
                </>
              )}
              {canManage && r.duplicate_flag === "suspected" && (
                <button onClick={() => onDismissFlag(r.reservation_id)}
                  className="flex items-center gap-1 px-3 py-1.5 border border-orange-300 text-orange-600 hover:bg-orange-50 text-xs rounded-lg font-medium">
                  <AlertTriangle className="h-3 w-3" /> Dismiss Flag
                </button>
              )}
              {r.duplicate_refs && r.duplicate_refs.length > 0 && (
                <p className="text-[10px] text-orange-600 bg-orange-50 px-2 py-1 rounded-lg w-full">
                  Similar to: {r.duplicate_refs.slice(0, 2).map(id => id.slice(0, 8)).join(", ")}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== PENDING TAB ====================
function PendingTab({ reservations, onApprove, onReject, actionLoading, onRefresh }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-700">{reservations.length} pending approval{reservations.length !== 1 ? "s" : ""}</h2>
        <button onClick={onRefresh} className="p-1.5 border rounded-lg hover:bg-gray-50 text-gray-500"><RefreshCw className="h-3.5 w-3.5" /></button>
      </div>
      {reservations.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <CheckCircle className="h-10 w-10 text-green-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No pending approvals</p>
        </div>
      ) : (
        reservations.map(r => (
          <div key={r.reservation_id} className="bg-white rounded-xl p-4 shadow-sm border border-amber-100 border-l-4 border-l-amber-400" data-testid={`pending-card-${r.reservation_id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <TypeBadge type={r.type} />
                  <AgeingBadge days={r.ageing_days} color={r.ageing_color} />
                  {r.duplicate_flag === "suspected" && <DupBadge />}
                </div>
                <h3 className="font-semibold text-[#09090b]">{r.product_code}</h3>
                <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 mt-0.5">
                  {r.batch_code && <span><Hash className="inline h-3 w-3 mr-0.5" />{r.batch_code}</span>}
                  <span>{r.quantity} {r.unit}</span>
                  <span className="font-medium text-gray-700">{r.salesman}</span>
                  <span>{r.commitment_date?.slice(0, 10)}</span>
                </div>
                {r.customer_name && r.customer_name !== "[Confidential]" && (
                  <p className="text-xs font-medium text-gray-700 mt-1">Customer: {r.customer_name}</p>
                )}
                {r.notes && <p className="text-xs text-gray-500 mt-1 italic">"{r.notes}"</p>}
                {r.lpo_number && <p className="text-xs text-blue-600 mt-0.5">LPO: {r.lpo_number}</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => onApprove(r.reservation_id)}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-xl font-semibold disabled:opacity-50"
                  data-testid={`pending-approve-${r.reservation_id}`}>
                  {actionLoading === r.reservation_id + "_approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Approve
                </button>
                <button onClick={() => onReject(r)}
                  disabled={!!actionLoading}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-xl font-semibold disabled:opacity-50"
                  data-testid={`pending-reject-${r.reservation_id}`}>
                  <XCircle className="h-4 w-4" /> Reject
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ==================== HISTORY TAB ====================
function HistoryTab({ history, total, page, onPageChange, canManage, isStaff }) {
  const ACTION_ICON = {
    created: { icon: Plus, color: "text-blue-500" },
    requested: { icon: FileText, color: "text-blue-500" },
    approved: { icon: CheckCircle, color: "text-green-500" },
    rejected: { icon: XCircle, color: "text-red-500" },
    edited: { icon: Edit2, color: "text-gray-500" },
    expired: { icon: AlertTriangle, color: "text-amber-500" },
    renewed: { icon: RotateCcw, color: "text-blue-500" },
    closed: { icon: Archive, color: "text-slate-500" },
    cancelled: { icon: X, color: "text-red-400" },
    duplicate_flagged: { icon: AlertTriangle, color: "text-orange-500" },
    duplicate_dismissed: { icon: CheckCircle, color: "text-orange-400" },
    auto_closed_confirmed: { icon: CheckCircle, color: "text-teal-500" },
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{total} entries</p>
        {canManage && (
          <a href={`${API}/reservations/history/export?format=xlsx`} download className="text-xs text-gray-500 flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1.5 hover:bg-gray-50">
            <Download className="h-3 w-3" /> Export
          </a>
        )}
      </div>
      {history.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No history</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-50">
          {history.map(h => {
            const ai = ACTION_ICON[h.action] || { icon: Hash, color: "text-gray-400" };
            const Icon = ai.icon;
            return (
              <div key={h.history_id} className="flex items-start gap-3 p-3 sm:p-4" data-testid={`history-${h.history_id}`}>
                <div className={`w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon className={`h-3.5 w-3.5 ${ai.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-[#09090b] capitalize">{h.action.replace(/_/g, " ")}</span>
                    <span className="text-[10px] text-gray-400">{h.performed_at?.slice(0, 16)?.replace("T", " ")}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">{h.performed_by}</p>
                  {h.details && <p className="text-xs text-gray-600 mt-0.5 italic">"{h.details}"</p>}
                  <p className="text-[10px] text-gray-400 mt-0.5">Ref: {h.reservation_id?.slice(0, 8)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {total > 50 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => onPageChange(page - 1)}
            className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 disabled:opacity-40">Prev</button>
          <span className="text-xs text-gray-500">Page {page}</span>
          <button disabled={page * 50 >= total} onClick={() => onPageChange(page + 1)}
            className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

// ==================== RESERVATION FORM MODAL ====================
function ReservationFormModal({ isOpen, onClose, editingRes, salesmen, sessionSalesman, userRole, canManage, onSuccess }) {
  const [step, setStep] = useState(1);
  const [formType, setFormType] = useState(editingRes?.type || "reservation");
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(editingRes?.batch_code || "");
  const [quantity, setQuantity] = useState(editingRes?.quantity?.toString() || "");
  const [customerName, setCustomerName] = useState(editingRes?.customer_name || "");
  const [salesman, setSalesman] = useState(editingRes?.salesman || sessionSalesman || "");
  const [commitDate, setCommitDate] = useState(editingRes?.commitment_date?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [lpoNumber, setLpoNumber] = useState(editingRes?.lpo_number || "");
  const [notes, setNotes] = useState(editingRes?.notes || "");
  const [expiryDays, setExpiryDays] = useState(editingRes?.default_expiry_days || 14);
  const [stockPreview, setStockPreview] = useState(null);
  const [dupWarning, setDupWarning] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const searchTimer = useRef(null);
  const isStaff = userRole === "staff";

  // Fetch product data if editing
  useEffect(() => {
    if (editingRes && editingRes.product_code) {
      setProductSearch(editingRes.product_code);
      // Fetch product info
      axios.get(`${API}/stock`, { params: { search: editingRes.product_code, limit: 1 } })
        .then(r => {
          const items = r.data.items || r.data || [];
          const prod = Array.isArray(items) ? items[0] : null;
          if (prod) {
            setSelectedProduct(prod);
            const parsed = (prod.dye_lots || []).map(dl => {
              const parts = dl.split(":");
              return { code: parts[0], available: parseFloat(parts[1] || 0) };
            }).filter(b => b.code);
            setBatches(parsed);
          }
        }).catch(() => {});
    }
  }, [editingRes]);

  const searchProducts = useCallback((q) => {
    if (!q.trim()) { setProductResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/stock`, { params: { search: q, limit: 50 } });
        const items = r.data.items || r.data || [];
        setProductResults(Array.isArray(items) ? items : []);
      } catch (e) { setProductResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, []);

  const selectProduct = (prod) => {
    setSelectedProduct(prod);
    setProductSearch(prod.description || prod.product_code);
    setProductResults([]);
    const parsed = (prod.dye_lots || []).map(dl => {
      const parts = dl.split(":");
      return { code: parts[0], available: parseFloat(parts[1] || 0) };
    }).filter(b => b.code);
    setBatches(parsed);
    setSelectedBatch("");
  };

  // Compute stock preview
  useEffect(() => {
    if (!selectedProduct) return;
    const pc = selectedProduct.product_code;
    axios.get(`${API}/reservations/stock-summary`).then(r => {
      const appData = r.data[pc] || { total: 0 };
      const erpAvailable = selectedProduct.available_qty || 0;
      const appReserved = appData.total || 0;
      const netAvailable = erpAvailable - appReserved;  // Allow negative to show true over-commitment
      const qty = parseFloat(quantity) || 0;
      setStockPreview({ available: erpAvailable, reserved: appReserved, netAvailable, after: netAvailable - qty });
    }).catch(() => {
      const available = selectedProduct.available_qty || 0;
      const qty = parseFloat(quantity) || 0;
      setStockPreview({ available, reserved: 0, netAvailable: available, after: available - qty });
    });
  }, [selectedProduct, quantity]);

  const handleSubmit = async () => {
    if (!selectedProduct) { toast.error("Please select a product"); return; }
    if (!quantity || parseFloat(quantity) <= 0) { toast.error("Please enter a valid quantity"); return; }
    if (!customerName.trim()) { toast.error("Please enter customer name"); return; }
    if (!salesman && !sessionSalesman) { toast.error("Please select a salesman"); return; }

    setSubmitting(true);
    try {
      const body = {
        type: formType,
        product_code: selectedProduct.product_code,
        batch_code: selectedBatch || null,
        quantity: parseFloat(quantity),
        unit: selectedProduct.unit || "SQM",
        customer_name: customerName.trim(),
        salesman: salesman || sessionSalesman,
        notes: notes || null,
        lpo_number: lpoNumber || null,
        commitment_date: commitDate,
        default_expiry_days: parseInt(expiryDays) || 14,
      };
      if (editingRes) {
        await axios.put(`${API}/reservations/${editingRes.reservation_id}`, body);
        toast.success("Reservation updated");
      } else {
        const res = await axios.post(`${API}/reservations`, body);
        if (res.data.over_limit) {
          toast.warning(`Over-limit: quantity exceeds net available stock. Reservation submitted for review.`);
        } else if (res.data.duplicate_flag === "suspected") {
          toast.warning("Similar reservation detected — flagged for review");
        } else {
          toast.success(isStaff ? "Reservation request submitted" : "Reservation created");
        }
        setDupWarning(res.data.duplicate_flag === "suspected" ? res.data : null);
      }
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-base font-bold text-[#09090b]">{editingRes ? "Edit Reservation" : "New Reservation"}</h2>
            {isStaff && <p className="text-xs text-gray-500">Submits for Logistics approval</p>}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-500" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Type Selector */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Type</label>
            <div className="flex gap-2">
              <button onClick={() => setFormType("reservation")}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${formType === "reservation" ? "bg-blue-50 border-blue-400 text-blue-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                data-testid="type-reservation">
                Reservation
              </button>
              <button onClick={() => setFormType("lpo_received")}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${formType === "lpo_received" ? "bg-red-50 border-red-400 text-red-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                data-testid="type-lpo">
                LPO Received
              </button>
            </div>
          </div>

          {/* Product Search */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Product</label>
            <div className="relative">
              <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[#DC2626] transition-colors">
                <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <input value={productSearch} onChange={e => { setProductSearch(e.target.value); searchProducts(e.target.value); setSelectedProduct(null); }}
                  placeholder="Search by name or product code…"
                  className="flex-1 text-sm outline-none bg-transparent" data-testid="product-search-input" />
                {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
              </div>
              {productResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-52 overflow-y-auto mt-1">
                  {productResults.map(p => (
                    <button key={p.product_code} onClick={() => selectProduct(p)}
                      className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors"
                      data-testid={`product-result-${p.product_code}`}>
                      <p className="text-sm font-medium text-[#09090b] truncate">{p.description || p.product_code}</p>
                      <p className="text-xs text-gray-500">{p.product_code} · {p.available_qty?.toFixed(1)} {p.unit} available</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedProduct && (
              <div className="mt-1.5 flex items-center gap-2 text-xs text-green-600">
                <CheckCircle className="h-3.5 w-3.5" />
                <span>{selectedProduct.description} — {selectedProduct.available_qty?.toFixed(1)} {selectedProduct.unit} available</span>
              </div>
            )}
          </div>

          {/* Batch */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Batch</label>
            {batches.length > 0 ? (
              <select value={selectedBatch} onChange={e => setSelectedBatch(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#DC2626]"
                data-testid="batch-select">
                <option value="">Select a batch…</option>
                {batches.map(b => (
                  <option key={b.code} value={b.code}>{b.code} — {b.available?.toFixed(1)} {selectedProduct?.unit || ""} available</option>
                ))}
              </select>
            ) : (
              <div className="border border-gray-100 rounded-xl px-3 py-2.5 text-sm text-gray-400 bg-gray-50">
                {selectedProduct ? "No specific batch (non-batched product)" : "Select product first"}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Quantity</label>
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[#DC2626]">
              <input type="number" min="0" step="0.01" value={quantity} onChange={e => setQuantity(e.target.value)}
                placeholder="0.00" className="flex-1 text-sm outline-none bg-transparent" data-testid="quantity-input" />
              <span className="text-sm font-medium text-gray-500 flex-shrink-0">{selectedProduct?.unit || "—"}</span>
            </div>
          </div>

          {/* Stock Visibility Preview */}
          {stockPreview && selectedProduct && (
            <div className={`rounded-xl p-3 border ${formType === "lpo_received" ? "border-red-100 bg-red-50" : "border-blue-100 bg-blue-50"}`}>
              <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Stock Preview</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-[10px] text-gray-500">Net Available</p><p className={`text-sm font-bold ${stockPreview.netAvailable < 0 ? "text-red-600" : "text-green-600"}`}>{(stockPreview.netAvailable ?? stockPreview.available)?.toFixed(1)}</p><p className="text-[10px] text-gray-400">{selectedProduct.unit}</p></div>
                <div><p className="text-[10px] text-gray-500">App Reserved</p><p className="text-sm font-bold text-amber-600">{stockPreview.reserved?.toFixed(1)}</p><p className="text-[10px] text-gray-400">{selectedProduct.unit}</p></div>
                <div><p className="text-[10px] text-gray-500">After Request</p><p className={`text-sm font-bold ${stockPreview.after < 0 ? "text-red-600" : "text-blue-600"}`}>{stockPreview.after?.toFixed(1)}</p><p className="text-[10px] text-gray-400">{selectedProduct.unit}</p></div>
              </div>
              {stockPreview.after < 0 && <p className="text-xs text-red-500 mt-1.5 text-center font-medium">Warning: quantity exceeds net available stock</p>}
            </div>
          )}

          {/* Customer + Salesman */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Customer Name <span className="text-gray-400 normal-case">(private)</span></label>
              <input value={customerName} onChange={e => setCustomerName(e.target.value)}
                placeholder="Customer / client name"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#DC2626]"
                data-testid="customer-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Salesman</label>
              {isStaff && sessionSalesman ? (
                <div className="border border-gray-100 bg-gray-50 rounded-xl px-3 py-2.5 text-sm text-gray-700">{sessionSalesman}</div>
              ) : (
                <select value={salesman} onChange={e => setSalesman(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#DC2626]"
                  data-testid="salesman-select">
                  <option value="">Select salesman…</option>
                  {salesmen.map(s => <option key={s.salesman_id} value={s.name}>{s.name}</option>)}
                </select>
              )}
            </div>
          </div>

          {/* LPO Number (shown when type = lpo_received) */}
          {formType === "lpo_received" && (
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
                LPO Number <span className="text-gray-400 normal-case">(leave blank if unknown)</span>
              </label>
              <input value={lpoNumber} onChange={e => setLpoNumber(e.target.value)}
                placeholder="e.g. LPO-2024-0123"
                className="w-full border border-red-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-red-400"
                data-testid="lpo-number-input" />
            </div>
          )}

          {/* Date + Expiry */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Commitment Date</label>
              <input type="date" value={commitDate} onChange={e => setCommitDate(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#DC2626]"
                data-testid="commit-date-input" />
            </div>
            {canManage && (
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Expiry (days)</label>
                <input type="number" min="1" max="365" value={expiryDays} onChange={e => setExpiryDays(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#DC2626]"
                  data-testid="expiry-days-input" />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={formType === "lpo_received" ? "Additional LPO details, delivery notes, etc." : "Optional notes…"}
              rows={2} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#DC2626] resize-none"
              data-testid="notes-input" />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="form-cancel-btn">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={submitting}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2 ${formType === "lpo_received" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
              data-testid="form-submit-btn">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingRes ? "Save Changes" : (isStaff ? "Submit Request" : "Create Reservation")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== REJECT MODAL ====================
function RejectModal({ reservation, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl">
        <h2 className="text-base font-bold text-[#09090b] mb-1">Reject Reservation</h2>
        <p className="text-xs text-gray-500 mb-3">Ref: #{reservation.reservation_id?.slice(0, 8)} — {reservation.product_code}</p>
        <textarea value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason for rejection (required)…" rows={3}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-red-400 resize-none mb-3"
          data-testid="reject-reason-input" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium">Cancel</button>
          <button onClick={() => reason.trim() && onConfirm(reservation.reservation_id, reason.trim())}
            disabled={!reason.trim() || loading}
            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
            data-testid="reject-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== RENEW MODAL ====================
function RenewModal({ reservation, onClose, onConfirm, loading }) {
  const [days, setDays] = useState(7);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl">
        <h2 className="text-base font-bold text-[#09090b] mb-1">Renew Reservation</h2>
        <p className="text-xs text-gray-500 mb-4">Ref: #{reservation.reservation_id?.slice(0, 8)} — {reservation.product_code}</p>
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-600 mb-2 block">Extension (1–7 days)</label>
          <div className="flex gap-2">
            {[1, 3, 7].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 ${days === d ? "border-blue-400 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-500"}`}
                data-testid={`renew-${d}d`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium">Cancel</button>
          <button onClick={() => onConfirm(reservation.reservation_id, days)} disabled={loading}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
            data-testid="renew-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : `Extend ${days}d`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== SALESMAN MANAGE MODAL ====================
function SalesmanManageModal({ salesmen, onClose, onRefresh }) {
  const [name, setName] = useState(""); const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const handleAdd = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/salesmen`, { name: name.trim(), email: email.trim() || null });
      toast.success("Salesman added"); setName(""); setEmail(""); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this salesman?")) return;
    try { await axios.delete(`${API}/salesmen/${id}`); toast.success("Deactivated"); onRefresh(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#09090b]">Manage Salesmen</h2>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none" data-testid="new-salesman-name" />
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (opt)" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none" data-testid="new-salesman-email" />
          <button onClick={handleAdd} disabled={saving || !name.trim()} className="px-3 py-2 bg-[#DC2626] text-white rounded-lg text-sm disabled:opacity-50" data-testid="add-salesman-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {salesmen.map(s => (
            <div key={s.salesman_id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50">
              <div><p className="text-sm font-medium">{s.name}</p>{s.email && <p className="text-xs text-gray-400">{s.email}</p>}</div>
              <button onClick={() => handleDeactivate(s.salesman_id)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded" data-testid={`deactivate-${s.salesman_id}`}>Remove</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== SO MATCH MODAL ====================
function SOMatchModal({ matches, onClose, onConfirm, actionLoading }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-md shadow-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-[#09090b]">ERP Sales Order Matches</h2>
            <p className="text-xs text-gray-500">Review potential matches — confirm to close</p>
          </div>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        {matches.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-8 text-gray-400 text-sm">No matches detected</div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-2">
            {matches.map(m => (
              <div key={m.reservation_id} className="border border-amber-200 bg-amber-50 rounded-xl p-3">
                <p className="text-sm font-semibold text-[#09090b]">{m.product_code}</p>
                <div className="text-xs text-gray-600 mt-0.5 space-y-0.5">
                  <p>Reservation qty: <strong>{m.reservation_qty}</strong> · ERP reserved: <strong>{m.erp_reserved_qty}</strong></p>
                  <p>Salesman: {m.salesman}</p>
                </div>
                <button onClick={() => onConfirm(m.reservation_id)}
                  disabled={!!actionLoading}
                  className="mt-2 w-full py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold"
                  data-testid={`confirm-close-${m.reservation_id}`}>
                  {actionLoading === m.reservation_id + "_confirm" ? <Loader2 className="h-3 w-3 animate-spin mx-auto" /> : "Confirm Close (ERP Matched)"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
